import { Pressable, StyleSheet, View } from "react-native";

import {
  radii,
  useTheme,
  useThemedStyles,
  type ThemePreference,
  type ThemeColors,
} from "~/styles/theme";
import { Icon } from "./Icon";

type ThemeToggleProps = {
  /** Called after the theme changes so the view layer can persist the choice
   *  elsewhere (e.g. to a profile row). The primitive only updates context. */
  onPreferenceChange?: (preference: ThemePreference) => void;
};

/** Compact control that cycles light → dark → system. */
export function ThemeToggle({ onPreferenceChange }: ThemeToggleProps = {}) {
  const { preference, scheme, setPreference } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);

  const icon =
    preference === "system"
      ? "monitor"
      : preference === "dark"
        ? "moon"
        : "sun";

  const cycle = () => {
    const next: ThemePreference =
      preference === "light"
        ? "dark"
        : preference === "dark"
          ? "system"
          : "light";
    setPreference(next);
    onPreferenceChange?.(next);
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Theme: ${preference} (currently ${scheme}). Tap to change.`}
      onPress={cycle}
      style={({ hovered, pressed }) => [
        stylesheet.button,
        hovered || pressed ? stylesheet.hover : null,
      ]}
    >
      <Icon name={icon} size="sm" />
      {preference === "system" ? <View style={stylesheet.dot} /> : null}
    </Pressable>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    button: {
      width: 38,
      height: 38,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    hover: { backgroundColor: colors.surface },
    dot: {
      position: "absolute",
      right: 5,
      bottom: 5,
      width: 5,
      height: 5,
      borderRadius: 2.5,
      backgroundColor: colors.primary,
    },
  });
